import React from 'react';
import { Formik, Form, Field, CccisdInput, CccisdFieldWrapper, CccisdSelect, CccisdToggle } from 'cccisd-formik';
import Table from 'cccisd-graphql-table';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import sitesQuery from './sites.graphql';
import youthsQuery from './youths.graphql';
import youthsBySupervisorQuery from './youthsBySupervisor.graphql';
import youthsByCareCoordinatorQuery from './youthsByCareCoordinator.graphql';
import careCoordinatorsQuery from './careCoordinators.graphql';
import careCoordinatorsSupervisorQuery from './careCoordinatorsSupervisor.graphql';
import RequiredFieldLabel from './RequiredFieldLabel';
import { states } from 'js/components/YouthRostering/AddYouthForm/AddressBlock';
import style from './style.css';

const Fortress = window.cccisd && window.cccisd.fortress;

const siteColumns = [
    {
        name: 'group.groupId',
        label: 'ID',
        sort: true,
        filter: true,
    },
    {
        name: 'group.label',
        label: 'Site',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.agency.group.label',
        label: 'Agency',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.organization.group.label',
        label: 'Organization',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.collaborator.group.label',
        label: 'Collaborator',
        sort: true,
        filter: true,
    },
];

const youthColumns = [
    {
        name: 'pawn.pawnId',
        label: 'ID',
        hideInTable: true,
    },
    {
        name: 'fields.wrapId',
        label: 'Wrap ID',
        sort: true,
        filter: true,
    },
    {
        name: 'fields.firstName',
        label: 'First Name',
        sort: true,
        filter: true,
    },
    {
        name: 'fields.lastName',
        label: 'Last Name',
        sort: true,
        filter: true,
    },
    {
        name: 'careCoordinatorFullName',
        label: 'Care Coordinator',
        sort: true,
        filter: true,
    },
    {
        name: 'caregiverFullName',
        label: 'Caregiver',
        sort: true,
        filter: true,
    },
    {
        name: 'fields.enrolledInSmartwrap',
        label: 'Enrolled in SMARTWrap',
        sort: true,
        filter: true,
        filterSettings: {
            type: 'selectbox',
            options: [{ label: 'Yes', value: '1' }],
        },
        render: ({ value, row }) => {
            if (value === '1' || value === true) {
                return 'Yes';
            }
            return '';
        },
    },
];

const careCoordinatorColumns = [
    {
        name: 'pawn.pawnId',
        label: 'ID',
        hideInTable: true,
    },
    {
        name: 'user.fullName',
        label: 'Care Coordinator',
        sort: true,
        filter: true,
    },
    {
        name: 'parentRoles.supervisor.user.fullNameWithUsername',
        label: 'Supervisor',
        sort: true,
        filter: true,
    },
    {
        name: 'fields.enrolledInSmartwrap',
        label: 'Enrolled in SMARTWrap',
        sort: true,
        filter: true,
        filterSettings: {
            type: 'selectbox',
            options: [{ label: 'Yes', value: '1' }],
        },
        render: ({ value, row }) => {
            if (value === '1' || value === true) {
                return 'Yes';
            }
            return '';
        },
    },
];

const stateOptions = [
    { value: '', label: '-- Select state --' },
    ...states.map(usState => ({ value: usState, label: usState })),
];

const CaregiverForm = props => {
    const { onSubmit, initialValues } = props;
    const currentRole = Fortress.user && Fortress.user.acting.role.handle;
    const isSupervisor = currentRole === 'supervisor';
    const isCareCoordinator = currentRole === 'careCoordinator';
    const isSiteAdmin = currentRole === 'siteAdmin';
    // Also hides the site picker for Site Admin (see the youth table below
    // for the one place this needs to stay narrower).
    const hasFixedSite = isSupervisor || isCareCoordinator || isSiteAdmin;

    const onHandleSubmit = async values => {
        await onSubmit({
            ...values,
            site: values.site[0],
            youth: (values.youth || []).map(youth => (youth && typeof youth === 'object' ? youth.pawn.pawnId : youth)),
            role: 'caregiver',
        });
    };

    const validate = values => {
        const errors = {};

        if (!values.caregiverName) {
            errors.caregiverName = 'Full Name is required.';
        }
        if (!values.caregiverPhone && values.receiveMessages) {
            errors.caregiverPhone = 'Phone number is required.';
        }
        if (values.caregiverPhone) {
            if (!/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(values.caregiverPhone)) {
                errors.caregiverPhone = 'Must be a valid phone number.';
            }
        }
        if (values.caregiverEmail && values.caregiverEmail.length > 0) {
            if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.caregiverEmail)) {
                errors.caregiverEmail = 'Invalid email address.';
            }
        }

        if (!hasFixedSite) {
            if (values.site.length < 1) {
                errors.site = 'Site is required.';
            }
            if (values.site.length > 1) {
                errors.site = 'You may select only 1 Site.';
            }
        }

        if (values.careCoordinator < 1) {
            errors.careCoordinator = 'Care Coordinator is required.';
        }
        if (values.careCoordinator.length > 1) {
            errors.careCoordinator = 'You may select only 1 Care Coordinator.';
        }

        return errors;
    };

    return (
        <Formik onSubmit={e => onHandleSubmit(e)} validate={e => validate(e)} initialValues={initialValues}>
            {({ handleSubmit, values, setFieldValue, isSubmitting }) => {
                return (
                    <Form>
                        <Field
                            name="caregiverName"
                            component={CccisdInput}
                            label={<RequiredFieldLabel isRequired>Full Name:</RequiredFieldLabel>}
                        />
                        <Field
                            name="caregiverPhone"
                            country="US"
                            component={CccisdInput}
                            label={
                                values.receiveMessages ? (
                                    <RequiredFieldLabel isRequired>Phone Number:</RequiredFieldLabel>
                                ) : (
                                    'Phone Number:'
                                )
                            }
                        />

                        <Field name="caregiverEmail" component={CccisdInput} label="Email:" />

                        <Field name="receiveMessages" component={CccisdToggle} label="Receives Messages" />
                        <Field
                            name="caregiverLanguage"
                            component={CccisdSelect}
                            label="Language:"
                            options={[
                                { value: '', label: '-- Choose language --' },
                                { value: 'en', label: 'English' },
                                { value: 'es', label: 'Spanish' },
                                { value: 'other', label: 'Other' },
                            ]}
                        />
                        {values.caregiverLanguage === 'other' && (
                            <Field name="caregiverSpecifyLanguage" component={CccisdInput} label="Specify Language:" />
                        )}

                        <Field name="caregiverAddress" component={CccisdInput} label="Mailing Address:" />
                        <div style={{ display: 'flex', gap: '1em' }}>
                            <Field name="caregiverCity" component={CccisdInput} label="City:" />
                            <Field
                                name="caregiverState"
                                component={CccisdSelect}
                                label="State:"
                                options={stateOptions}
                            />
                            <Field name="caregiverZipCode" component={CccisdInput} label="Zip Code:" />
                        </div>
                        {!hasFixedSite && (
                            <Field name="site" enableReinitialize>
                                {({ field, form }) => (
                                    <CccisdFieldWrapper
                                        field={field}
                                        form={form}
                                        label={<RequiredFieldLabel isRequired>Site:</RequiredFieldLabel>}
                                    >
                                        <Table
                                            query={sitesQuery}
                                            rowKey="group.groupId"
                                            columns={siteColumns}
                                            csvFilename="Sites.csv"
                                            showRowActions={false}
                                            perPage={10}
                                            onRowClick={row => {
                                                setFieldValue('site', [row['group.groupId']]);
                                                setFieldValue('youth', []);
                                            }}
                                            setRowClass={row =>
                                                row['group.groupId'] === field.value[0] && style.selectedRow
                                            }
                                        />
                                    </CccisdFieldWrapper>
                                )}
                            </Field>
                        )}

                        {values.site && values.site.length === 1 && (
                            <Field name="youth" enableReinitialize>
                                {({ field, form }) => {
                                    return (
                                        <CccisdFieldWrapper
                                            field={field}
                                            form={form}
                                            label={<RequiredFieldLabel>Youth:</RequiredFieldLabel>}
                                        >
                                            <Table
                                                query={
                                                    // eslint-disable-next-line no-nested-ternary
                                                    isSupervisor
                                                        ? youthsBySupervisorQuery
                                                        : isCareCoordinator
                                                        ? youthsByCareCoordinatorQuery
                                                        : youthsQuery
                                                }
                                                rowKey="pawn.pawnId"
                                                columns={youthColumns}
                                                csvFilename="Youths.csv"
                                                showRowActions={false}
                                                keepSelectedOnFiltering
                                                rowSelectedCallback={selectedRows => {
                                                    if (!_isEqual(selectedRows, field.value)) {
                                                        setFieldValue('youth', selectedRows);
                                                    }
                                                }}
                                                selectedRows={
                                                    // if the site was just changed, youth should reset; otherwise pre-check the caregiver's existing youth
                                                    !_isEqual(form.values.site, initialValues.site)
                                                        ? []
                                                        : initialValues.youth.map(youth => {
                                                              return youth.pawn.pawnId;
                                                          })
                                                }
                                                graphqlVariables={
                                                    isSupervisor || isCareCoordinator
                                                        ? { pawnId: Fortress.user.acting.id }
                                                        : { siteId: values.site[0] }
                                                }
                                                rowActions={[
                                                    {
                                                        name: 'selectYouth',
                                                        title: 'Link to Youth',
                                                        action: () => {},
                                                    },
                                                ]}
                                            />
                                        </CccisdFieldWrapper>
                                    );
                                }}
                            </Field>
                        )}
                        {values.site &&
                            (Array.isArray(values.site) ? values.site.length === 1 : values.site) &&
                            currentRole !== 'careCoordinator' && (
                                <>
                                    <Field name="careCoordinator" enableReinitialize>
                                        {({ field, form }) => {
                                            return (
                                                <CccisdFieldWrapper
                                                    field={field}
                                                    form={form}
                                                    label={
                                                        <RequiredFieldLabel isRequired>
                                                            Care Coordinator:
                                                        </RequiredFieldLabel>
                                                    }
                                                >
                                                    <Table
                                                        query={
                                                            currentRole === 'supervisor'
                                                                ? careCoordinatorsSupervisorQuery
                                                                : careCoordinatorsQuery
                                                        }
                                                        rowKey="pawn.pawnId"
                                                        columns={careCoordinatorColumns}
                                                        csvFilename="Care Coordinators.csv"
                                                        showRowActions={false}
                                                        rowSelectedCallback={selectedRows => {
                                                            if (!_isEqual(selectedRows, field.value)) {
                                                                setFieldValue('careCoordinator', selectedRows);
                                                                if (currentRole === 'supervisor') {
                                                                    setFieldValue('youth', []);
                                                                }
                                                            }
                                                        }}
                                                        selectedRows={
                                                            // if the site is edited, careCoordinator should be []
                                                            !_isEqual(form.values.site, initialValues.site)
                                                                ? field.value
                                                                : initialValues.careCoordinator
                                                        }
                                                        graphqlVariables={
                                                            currentRole !== 'supervisor'
                                                                ? { siteId: values.site[0] }
                                                                : {}
                                                        }
                                                        maxSelectableRows={1}
                                                        rowActions={[
                                                            {
                                                                name: 'selectCareCoordinator',
                                                                title: 'Link to Care Coordinator',
                                                                action: () => {},
                                                            },
                                                        ]}
                                                    />
                                                </CccisdFieldWrapper>
                                            );
                                        }}
                                    </Field>
                                </>
                            )}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: '1em',
                            }}
                        >
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handleSubmit()}
                                disabled={isSubmitting}
                            >
                                Submit
                            </button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

CaregiverForm.propTypes = {
    onSubmit: PropTypes.func,
    initialValues: PropTypes.object,
};

export default CaregiverForm;
